import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Container, CardDeck, Card } from 'react-bootstrap'
import { graphql } from 'gatsby'

import DataDiffImage from '../images/data-diff/data-diff.png'
import PointsMonitoring from '../images/data_monitoring/points-monitoring.png'
import PointsMonitoringMobile from '../images/data_monitoring/points-monitoring-mobile.png'
import RegressionTesting from '../images/data-diff/regressio-testing.png'
import GitIntegration from '../images/data-diff/git-integration.png'
import PointsGit from '../images/data-diff/points-git.png'
import UseCases from '../images/data-diff/use-cases.png'
import LikeImage from '../images/data-diff/like.svg'
import LoopImage from '../images/data-diff/loop.svg'
import StoreImage from '../images/data-diff/store.svg'
import UpDownImage from '../images/data-diff/up-down.svg'
import PointsV2Image from '../images/home/points-v2.svg'
import PointsV3Image from '../images/home/points-v3.svg'
import CloudImage from '../images/home/cloud.svg'
import ArrowsImage from '../images/home/arrows.svg'
import ArrowsAngleImage from '../images/data-diff/arrows-angle.svg'
import CheckMarkImage from '../images/home/check-mark.svg'
import IntegrationsImage from '../images/home/integrations-block.svg'
import IntegrationsMobile from '../images/home/integrations-mobile.png'
import ArrowsIconImage from '../images/home/arrows-icon.svg'
import ManIconImage from '../images/data-diff/man-icon.svg'
import DollarIconImage from '../images/home/dollar-icon.svg'

import { MetaData } from '../components/common/meta'
import { Testimonials, ActNow, Layout, ButtonAnalytics } from '../components/common'
import { request } from 'graphql-request'
import { graphCMSUrl } from '../utils/siteConfig'

/**
 * Main index page (home page)
 *
 * Loads all posts from Ghost and uses pagination to navigate through them.
 * The number of posts that should appear per page can be setup
 * in /utils/siteConfig.js under `postsPerPage`.
 *
 */
const DataDiff = ({ data, location }) => {
    const page = data.ghostPage || {}
    const [testimonialList, setTestimonialList] = useState([])

    useEffect(() => {
        const fetchTestimonials = async () => {
            const { testimonials } = await request(
                graphCMSUrl,
                `
                {
                     testimonials(stage: PUBLISHED) {
                        id
                        personName
                        personRole
                        blurb
                        companyLogo {
                            url
                        }
                        personAvatar {
                            fileName
                            url
                        }
                    }
                  }
                `
            )
            setTestimonialList(testimonials)
        }

        fetchTestimonials()
    }, [])
    return (
        <>
            <MetaData
                data={data}
                location={location}
                type="website"
            />
            <Layout footerClass="large" headerClass="header-grey">
                <section className="metric diff">
                    <Container>
                        <Row style={{ textAlign: 'center' }}>
                            <Col>
                                <span className="sub-title">{page.title}</span>
                                <h2>
                                    1-click regression testing for ETL
                                </h2>
                                <p className="general-content">
                                    Verify data changes across billions of rows
                                </p>
                                <div className="metric-image-block">
                                    <img src={DataDiffImage} alt="1-click regression testing for ETL" />
                                </div>
                                <img className="left-points-desktop" src={PointsMonitoring} alt="points monitoring"/>
                                <img className="left-points-mobile" src={PointsMonitoringMobile} alt="points monitoring"/>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <section className="regression-testing">
                    <Container>
                        <Row>
                            <Col md={6} sm={12}>
                                <img className="left-block" src={RegressionTesting}/>
                            </Col>
                            <Col md={6} sm={12} className="right-column">
                                <span className="sub-title">Regression Testing</span>
                                <h2>
                                    100% test coverage for data
                                </h2>
                                <p className="general-content">
                                    Data Diff helps you avoid regressions in ETL by showing you how data changes across all rows and columns when you modify code or copy data between systems
                                </p>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <section className="git-integration">
                    <Container>
                        <Row>
                            <Col md={5} className="right-column">
                                <span className="sub-title">Git & CI Integration</span>
                                <h2>
                                    Automate data QA
                                </h2>
                                <p className="general-content">
                                    Integrate Datafold in your repo, CI & code review process to protect your codebase from breaking changes
                                </p>
                            </Col>
                            <Col md={7}>
                                <img className="right-block" src={GitIntegration} alt="Git integration"/>
                            </Col>
                        </Row>
                    </Container>
                    <img className="git-points" src={PointsGit} alt="git points" />
                </section>
                <section className="use-cases">
                    <Container>
                        <Row style={{ textAlign: 'center' }}>
                            <Col>
                                <span className="sub-title">Use cases</span>
                                <h2>
                                    Automated QA
                                </h2>
                                <p
                                    className="general-content"
                                    style={{ maxWidth: '394px' }}
                                >
                                    Increase reliability of your data transfers and data transformation workflows
                                </p>
                            </Col>
                        </Row>
                        <Row className="block-size first">
                            <Col lg={4}>
                                <LoopImage/>
                                <div className="title">Regression testing</div>
                                <div className="description">See how changes in your code affect the produced data</div>
                            </Col>
                            <Col lg={5}>
                                <img className="use-cases-image" src={UseCases} alt="use cases" />
                            </Col>
                            <Col lg={3}>
                                <LikeImage/>
                                <div className="title">Code Reviews</div>
                                <div className="description">Eliminate errors and
                                    accelerate team velocity</div>
                            </Col>
                        </Row>
                        <Row className="block-size">
                            <Col lg={4}>
                                <UpDownImage/>
                                <div className="title">Data transfer validation</div>
                                <div className="description">Ensure consistency when copying data between databases</div>
                            </Col>
                            <Col lg={5}></Col>
                            <Col lg={3}>
                                <StoreImage/>
                                <div className="title">ETL Migrations</div>
                                <div className="description">Easily detect and
                                    reconcile differences when migrating ETL to a new data warehouse</div>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <section className="data-monitoring diff">
                    <Container>
                        <Row style={{ textAlign: 'center' }}>
                            <Col>
                                <span className="sub-title">Best Practices</span>
                                <h2>
                                    From 0 to 100% test coverage for your ETL
                                </h2>
                                <p className="general-content">
                                    Data Diff gives you visibility into all changes introduced by code modification or data transfer without requiring you to write test cases manually
                                </p>
                                <CardDeck className="card-block col-xl-11 col-md-12">
                                    <Card>
                                        <ArrowsAngleImage/>
                                        <Card.Body>
                                            <Card.Title>Scales with your data</Card.Title>
                                            <Card.Text as="div">
                                                <p><CheckMarkImage/><span>Work with billion-row tables</span></p>
                                                <p><CheckMarkImage/><span>Minimizes costs through sampling</span></p>
                                                <p><CheckMarkImage/><span>Volume-neutral pricing</span></p>
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                    <Card>
                                        <CloudImage/>
                                        <Card.Body>
                                            <Card.Title>Enterprise ready</Card.Title>
                                            <Card.Text as="div">
                                                <p><CheckMarkImage/><span>{'Deployment in <30 min '}</span></p>
                                                <p><CheckMarkImage/><span>Integrates with SSO providers </span></p>
                                                <p><CheckMarkImage/><span>Security & Privacy compliant</span></p>
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                    <Card>
                                        <ArrowsImage/>
                                        <Card.Body>
                                            <Card.Title>Immediate Impact</Card.Title>
                                            <Card.Text as="div">
                                                <p><CheckMarkImage/><span>Save hours per weeks</span></p>
                                                <p><CheckMarkImage/><span>Minimize risk</span></p>
                                                <p><CheckMarkImage/><span>Increase team velocity</span></p>
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                </CardDeck>
                            </Col>
                        </Row>
                    </Container>
                    <PointsV2Image className="left-points"/>
                </section>
                {(testimonialList && testimonialList.length > 0) && (
                    <section className="happy-customers">
                        <Container>
                            <Row style={{ textAlign: 'center' }}>
                                <Col>
                                    <span className="sub-title">Happy Customers</span>
                                    <h2>
                                    Testimonials
                                    </h2>
                                    <div className="card-slider col-xl-9 col-md-10">
                                        <Testimonials testimonials={testimonialList}/>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </section>)}
                <section className="integrations">
                    <Container>
                        <Row style={{ textAlign: 'center' }}>
                            <Col>
                                <span className="sub-title">Integrations</span>
                                <h2>
                                    Missing puzzle in your data stack
                                </h2>
                                <p
                                    className="general-content"
                                    style={{ maxWidth: '527px' }}
                                >
                                    Datafold seamlessly plugs in all major SQL data warehouses and ETL tools
                                </p>
                                <div>
                                    <IntegrationsImage className="integrations-image"/>
                                    <img src={IntegrationsMobile} alt="integrations" className="integrations-image-mobile" />
                                </div>
                            </Col>
                        </Row>
                        <PointsV3Image className="right-points"/>
                    </Container>
                </section>
                <section className="immediate-business-impact">
                    <Container>
                        <Row style={{ textAlign: 'center' }}>
                            <Col>
                                <span className="sub-title">Immediate business impact</span>
                                <h2>
                                    Improved data quality benefits everyone
                                </h2>
                                <CardDeck className="card-block col-xl-11 col-md-12">
                                    <Card>
                                        <ArrowsIconImage/>
                                        <Card.Body>
                                            <Card.Title>Data Developer</Card.Title>
                                            <Card.Text as="div">
                                                <p><CheckMarkImage/><span>Deploy with confidence</span></p>
                                                <p><CheckMarkImage/><span>Eliminate toil work</span></p>
                                                <p><CheckMarkImage/><span>Focus on creative tasks</span></p>
                                                <p><CheckMarkImage/><span>Increase productivity</span></p>
                                            </Card.Text>
                                        </Card.Body>
                                        <Card.Footer>
                                            <ButtonAnalytics
                                                href="https://app.datafold.com/org-signup"
                                                target="_blank"
                                                variant="primary"
                                                size="lg"
                                                action_name="Get Started for free (Immediate business impact)"
                                            >
                                                Get Started for free
                                            </ButtonAnalytics>
                                        </Card.Footer>
                                    </Card>
                                    <Card>
                                        <ManIconImage/>
                                        <Card.Body>
                                            <Card.Title>Data Team Manager</Card.Title>
                                            <Card.Text as="div">
                                                <p><CheckMarkImage/><span>Prevent data incidents</span></p>
                                                <p><CheckMarkImage/><span>Establish data quality culture</span></p>
                                                <p><CheckMarkImage/><span>Increase team velocity</span></p>
                                                <p><CheckMarkImage/><span>Improve stakeholder trust</span></p>
                                            </Card.Text>

                                        </Card.Body>
                                        <Card.Footer>
                                            <ButtonAnalytics href="/calculator" variant="primary" size="lg" action_name="Estimate Impact">
                                                Estimate Impact
                                            </ButtonAnalytics>
                                        </Card.Footer>
                                    </Card>
                                    <Card>
                                        <DollarIconImage/>
                                        <Card.Body>
                                            <Card.Title>Business User</Card.Title>
                                            <Card.Text as="div">
                                                <p><CheckMarkImage/><span>Be confident in data</span></p>
                                                <p><CheckMarkImage/><span>Minimize business risk</span></p>
                                                <p><CheckMarkImage/><span>Get data faster</span></p>
                                                <p style={{ opacity: 0 }}>none</p>
                                            </Card.Text>
                                        </Card.Body>
                                        <Card.Footer>
                                            <ButtonAnalytics
                                                href="https://app.datafold.com/login?next=https%3A%2F%2Fapp.datafold.com%2F"
                                                variant="primary"
                                                target="_blank"
                                                size="lg"
                                                action_name="See a live Example"
                                            >
                                                See a live Example
                                            </ButtonAnalytics>
                                        </Card.Footer>
                                    </Card>
                                </CardDeck>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <ActNow/>
            </Layout>
        </>
    )
}

DataDiff.propTypes = {
    data: PropTypes.shape({
        ghostPage: PropTypes.object.isRequired,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
    pageContext: PropTypes.object,
}

export default DataDiff

// This page query loads all posts sorted descending by published date
// The `limit` and `skip` values are used for pagination
export const pageQuery = graphql`
  query DataDiffQuery($slug: String!) {
        ghostPage(slug: { eq: $slug }) {
            ...GhostPageFields
        }
    }
`
